"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { readPrefillParams, readUtmParams, trackEvent } from "@/lib/tracking";

// セッションID生成（ブラウザセッションごとにユニーク）
const generateSessionId = (): string => {
  const existingId = typeof window !== 'undefined' ? sessionStorage.getItem('diagnosis_session_id') : null;
  if (existingId) return existingId;
  const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('diagnosis_session_id', newId);
  }
  return newId;
};

// ファネルステップをバックエンドに送信
const trackFunnelStep = async (
  sessionId: string,
  step: number,
  data: {
    worry?: string;
    text_length?: number;
    email?: string;
    is_embed?: boolean;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
  }
) => {
  try {
    const apiUrl = typeof window !== 'undefined' && window.location.hostname === 'truemee.jp'
      ? 'https://truemee.jp/api'
      : (process.env.NEXT_PUBLIC_API_URL || 'https://truemee.jp/api');

    await fetch(`${apiUrl}/v2/diagnosis/funnel/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        step,
        ...data,
      }),
    });
  } catch (error) {
    // トラッキングエラーは無視（ユーザー体験に影響させない）
    console.warn('Funnel tracking error:', error);
  }
};

const worryDisplayFromParam = (worryParam?: string | null): string | undefined => {
  if (!worryParam) return undefined;
  const normalized = worryParam.toLowerCase();
  const mapping: Record<string, string> = {
    failing_screening: "書類選考で落ちることが多い",
    "書類選考で落ちることが多い": "書類選考で落ちることが多い",
    no_interviews: "面接で深掘りされると答えられない",
    "面接で深掘りされると答えられない": "面接で深掘りされると答えられない",
    first_resume: "自分の実績のアピール方法が不明",
    "自分の実績のアピール方法が不明": "自分の実績のアピール方法が不明",
    improve_quality: "未経験職種への挑戦で書き方が分からない",
    "未経験職種への挑戦で書き方が分からない": "未経験職種への挑戦で書き方が分からない",
  };
  return mapping[normalized];
};

const worryToApiFormat = (worry: string): string => {
  const mapping: Record<string, string> = {
    "書類選考で落ちることが多い": "failing_screening",
    "面接で深掘りされると答えられない": "no_interviews",
    "自分の実績のアピール方法が不明": "first_resume",
    "未経験職種への挑戦で書き方が分からない": "improve_quality",
  };
  return mapping[worry] || "failing_screening";
};

const ArrowRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const SearchIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

const LockSmallIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <path d="M22 4 12 14.01l-3-3" />
  </svg>
);

const ClockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const getDiagnosisContent = (worry: string) => {
  switch (worry) {
    case "書類選考で落ちることが多い":
      return {
        score: 42,
        type: "「量産型」経歴書リスク",
        desc: "あなたの強みが定型文に埋もれ、AIフィルターや採用担当者に「その他大勢」として処理されている可能性があります。",
        points: [
          "差別化要素が不足しており、印象に残らない...",
          "「頑張りました」以上の具体的プロセスが見えない...",
          "企業が求めるキーワードとのマッチ度が低い...",
        ],
      };
    case "面接で深掘りされると答えられない":
      return {
        score: 55,
        type: "「論理乖離（ハリボテ）」リスク",
        desc: "書類の記載内容と、あなたの語れるロジックにギャップがあります。このままだと面接で「信頼性」を損なう危険があります。",
        points: [
          "実績に対する「再現性」の証明が弱い...",
          "「なぜその行動をとったか」の動機付けが曖昧...",
          "成果とプロセスの因果関係が説明できていない...",
        ],
      };
    case "自分の実績のアピール方法が不明":
      return {
        score: 48,
        type: "「過小評価」リスク",
        desc: "本来の実力よりも低く見積もられています。当たり前だと思っている業務を「価値ある成果」として翻訳できていません。",
        points: [
          "定量的な成果（数値）への変換ができていない...",
          "受動的な表現が多く、主体性が伝わりにくい...",
          "隠れた「マネジメント要素」が記述されていない...",
        ],
      };
    case "未経験職種への挑戦で書き方が分からない":
      return {
        score: 35,
        type: "「ミスマッチ」リスク",
        desc: "熱意は伝わりますが、過去の経験が新しい職種でどう活きるか（ポータブルスキル）が翻訳されておらず、即戦力に見えません。",
        points: [
          "異業界でも通用する「汎用スキル」の抽出不足...",
          "過去の実績と志望動機の接続が弱い...",
          "未経験をカバーする「学習力」の根拠がない...",
        ],
      };
    default:
      return {
        score: 58,
        type: "具体性不足（埋没リスク）",
        desc: "あなたの経歴書は「強みの埋没」を起こしている可能性が高いです。具体的な修正箇所が特定されました。",
        points: [
          "「貢献しました」という表現が多すぎます...",
          "数値的根拠が不足しており、再現性が...",
          "リーダー経験の記述において、具体的な...",
        ],
      };
  }
};

export default function DiagnosisForm() {
  const [formStep, setFormStep] = useState(1);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisText, setAnalysisText] = useState("テキスト解析を開始...");
  const [utmParams, setUtmParams] = useState<Record<string, string | undefined>>({});
  const [isEmbedMode, setIsEmbedMode] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [formData, setFormData] = useState({
    worry: "",
    resume_text: "",
    name: "",
    email: "",
  });
  const analysisTimerRef = useRef<number | null>(null);

  // ファネルトラッキング用のヘルパー関数
  const trackStep = useCallback((step: number, extraData: Record<string, any> = {}) => {
    if (!sessionId) return;
    trackFunnelStep(sessionId, step, {
      is_embed: isEmbedMode,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_content: utmParams.utm_content,
      ...extraData,
    });
  }, [sessionId, isEmbedMode, utmParams]);

  useEffect(() => {
    // セッションID生成
    const sid = generateSessionId();
    setSessionId(sid);

    // Embed modeの検出（URLに/embed/が含まれているか）
    const isEmbed = typeof window !== "undefined" && window.location.pathname.includes("/embed/");
    setIsEmbedMode(isEmbed);

    const utm = readUtmParams();
    const prefill = readPrefillParams();
    const worryPrefill = worryDisplayFromParam(prefill.worry);

    setUtmParams(utm);
    setFormData((prev) => ({
      ...prev,
      name: prefill.name || prev.name,
      email: prefill.email || prev.email,
      resume_text: prefill.resume_text || prev.resume_text,
      worry: worryPrefill || prev.worry,
    }));

    if (worryPrefill) {
      setFormStep(2);
    }

    trackEvent("diagnosis_form_view", {
      ...utm,
      worry: worryPrefill || undefined,
      embed_mode: isEmbed,
    });

    // Step 0: ページ表示をトラッキング
    trackFunnelStep(sid, 0, {
      is_embed: isEmbed,
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_content: utm.utm_content,
    });

    return () => {
      if (analysisTimerRef.current) {
        clearInterval(analysisTimerRef.current);
      }
    };
  }, []);

  const handleOptionSelect = (key: string, value: string) => {
    setFormData({ ...formData, [key]: value });
    setFormStep(formStep + 1);

    // Step 1: 悩み選択をトラッキング
    trackStep(1, { worry: worryToApiFormat(value) });

    trackEvent("diagnosis_select_worry", {
      ...utmParams,
      worry: value,
    });
  };

  const handleTextSubmit = () => {
    if (!formData.resume_text || formData.resume_text.length < 10) {
      alert("診断のために、もう少し詳しく実績を入力してください（箇条書きでも構いません）。");
      return;
    }
    setFormStep(3);
    setIsAnalyzing(true);

    // Step 2: 実績入力完了をトラッキング
    trackStep(2, {
      worry: worryToApiFormat(formData.worry),
      text_length: formData.resume_text.length,
    });

    let progress = 0;
    if (analysisTimerRef.current) {
      clearInterval(analysisTimerRef.current);
    }
    analysisTimerRef.current = window.setInterval(() => {
      progress += 2;
      setAnalysisProgress(progress);

      if (progress < 30) setAnalysisText("具体性を解析中...");
      else if (progress < 60) setAnalysisText("悩みとの整合性をチェック中...");
      else if (progress < 85) setAnalysisText("採用担当者の視点でスコアリング中...");
      else setAnalysisText("改善レポート生成中...");

      if (progress >= 100) {
        if (analysisTimerRef.current) {
          clearInterval(analysisTimerRef.current);
        }
        setIsAnalyzing(false);
        setFormStep(4);

        // Step 3: スコア算出完了をトラッキング
        trackStep(3, {
          worry: worryToApiFormat(formData.worry),
          text_length: formData.resume_text.length,
        });
      }
    }, 40);

    trackEvent("diagnosis_text_submitted", {
      ...utmParams,
      text_length: formData.resume_text.length,
    });
  };

  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = getDiagnosisContent(formData.worry);
    // embed modeまたは本番環境では常に本番APIを使用
    const apiUrl = isEmbedMode || typeof window !== "undefined" && window.location.hostname === "truemee.jp"
      ? "https://truemee.jp/api"
      : (process.env.NEXT_PUBLIC_API_URL || "https://truemee.jp/api");

    const payload = {
      name: formData.name,
      email: formData.email,
      worry: worryToApiFormat(formData.worry),
      resume_text: formData.resume_text,
      calculated_score: result.score,
      diagnosis_type: result.type,
      diagnosis_desc: result.desc,
      improvement_points: result.points,
    };

    trackEvent("diagnosis_submit", {
      ...utmParams,
      worry: payload.worry,
      embed_mode: isEmbedMode,
    });

    try {
      const response = await fetch(`${apiUrl}/v2/diagnosis/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // レート制限エラー（429）の場合
      if (response.status === 429) {
        const data = await response.json();
        alert(data.error || "送信回数の上限に達しました。5分後に再度お試しください。");
        trackEvent("diagnosis_rate_limited", {
          ...utmParams,
          status: response.status,
        });
        return;
      }

      if (!response.ok) {
        console.error("診断結果の送信に失敗しました:", await response.text());
        trackEvent("diagnosis_submit_error", {
          ...utmParams,
          status: response.status,
        });
        return;
      }
      trackEvent("diagnosis_submit_success", {
        ...utmParams,
        worry: payload.worry,
        embed_mode: isEmbedMode,
      });

      // Step 4: 登録完了をトラッキング
      trackStep(4, {
        worry: payload.worry,
        text_length: formData.resume_text.length,
        email: formData.email,
      });
    } catch (error) {
      console.error("診断結果送信エラー:", error);
      trackEvent("diagnosis_submit_error", {
        ...utmParams,
        error_message: String(error),
      });
    }

    // Embed modeの場合は完了画面を表示、通常モードはリダイレクト
    if (isEmbedMode) {
      setFormStep(5);
      // 親ウィンドウにpostMessageで完了を通知
      if (window.parent !== window) {
        window.parent.postMessage({
          type: "truemee_diagnosis_complete",
          email: formData.email,
          name: formData.name,
        }, "*");
      }
    } else {
      const registerUrl = `/auth/register?email=${encodeURIComponent(formData.email)}&name=${encodeURIComponent(formData.name)}`;
      window.location.href = registerUrl;
    }
  };

  const renderFormStep = () => {
    if (formStep === 3) {
      return (
        <div style={{ textAlign: "center", padding: "48px 16px", backgroundColor: "white", borderRadius: "12px" }}>
          <div style={{ position: "relative", display: "inline-block", marginBottom: "24px" }}>
            <div
              className="animate-spin"
              style={{
                width: "80px",
                height: "80px",
                border: "4px solid transparent",
                borderTopColor: "#ed7243",
                borderBottomColor: "#ed7243",
                borderRadius: "50%",
              }}
            />
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", color: "#ed7243" }}>
              <SearchIcon />
            </div>
          </div>
          <h3 style={{ fontSize: "20px", fontWeight: "bold", color: "#333", marginBottom: "8px" }}>{analysisText}</h3>
          <div style={{ width: "100%", maxWidth: "280px", margin: "16px auto 0", backgroundColor: "#e5e5e5", borderRadius: "9999px", height: "12px", overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                backgroundColor: "#ed7243",
                borderRadius: "9999px",
                transition: "width 0.1s linear",
                width: `${analysisProgress}%`,
              }}
            />
          </div>
          <p style={{ color: "#999", fontSize: "12px", marginTop: "8px" }}>{analysisProgress}% 完了</p>
        </div>
      );
    }

    switch (formStep) {
      case 1:
        return (
          <div>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <span style={{ backgroundColor: "#dbeafe", color: "#1d4ed8", fontSize: "12px", fontWeight: "bold", padding: "4px 12px", borderRadius: "9999px" }}>STEP 1/3</span>
              <h3 style={{ fontSize: "18px", fontWeight: "bold", color: "#333", marginTop: "8px" }}>現在の選考における「最大の悩み」は？</h3>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {["書類選考で落ちることが多い", "面接で深掘りされると答えられない", "自分の実績のアピール方法が不明", "未経験職種への挑戦で書き方が分からない"].map((option) => (
                <button
                  key={option}
                  onClick={() => handleOptionSelect("worry", option)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "16px 20px",
                    borderRadius: "12px",
                    border: "2px solid #f3f4f6",
                    backgroundColor: "white",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    transition: "all 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#ed7243";
                    e.currentTarget.style.backgroundColor = "#fff7ed";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "#f3f4f6";
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  <span style={{ fontWeight: "bold", color: "#374151" }}>{option}</span>
                  <span style={{ color: "#d1d5db" }}>
                    <ArrowRightIcon />
                  </span>
                </button>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <button
              onClick={() => setFormStep(1)}
              style={{
                fontSize: "14px",
                color: "#9ca3af",
                marginBottom: "16px",
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              ← 戻る
            </button>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <span style={{ backgroundColor: "#dbeafe", color: "#1d4ed8", fontSize: "12px", fontWeight: "bold", padding: "4px 12px", borderRadius: "9999px" }}>STEP 2/3</span>
              <h3 style={{ fontSize: "18px", fontWeight: "bold", color: "#333", marginTop: "8px" }}>今お持ちの実績文をそのまま貼り付けてください</h3>
              <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                ※職務経歴書の「職務要約」や「自己PR」欄の文章をコピペでOK！
                <br />
                ※箇条書きでも、自信がない文章でもそのままで大丈夫です。
              </p>
              <p style={{ fontSize: "11px", color: "#ed7243", marginTop: "8px", fontWeight: "bold" }}>※書類通過率を劇的に変える「あなた専用の診断結果」は、この実績情報から導き出されます。</p>
            </div>

            <div style={{ position: "relative", marginBottom: "24px" }}>
              <textarea
                style={{
                  width: "100%",
                  height: "160px",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "2px solid #e5e7eb",
                  fontSize: "14px",
                  backgroundColor: "f9fafb",
                  resize: "none",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                placeholder={`（入力例）\n法人営業として3年間勤務。\n昨年度はチームリーダーとしてメンバー5名をマネジメントし、\n部内の売上目標120%達成に貢献しました。`}
                onChange={(e) => setFormData({ ...formData, resume_text: e.target.value })}
                value={formData.resume_text}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "#ed7243";
                  e.currentTarget.style.backgroundColor = "white";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
              />
              <div style={{ position: "absolute", bottom: "12px", right: "12px", fontSize: "12px", color: "#9ca3af", display: "flex", alignItems: "center", gap: "4px" }}>
                <EditIcon />
                AIが具体性を判定します
              </div>
            </div>

            <button
              onClick={handleTextSubmit}
              style={{
                width: "100%",
                backgroundColor: "#ed7243",
                color: "white",
                fontWeight: "bold",
                padding: "16px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                fontSize: "16px",
                transition: "transform 0.2s, background-color 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#dc5f35";
                e.currentTarget.style.transform = "scale(1.02)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#ed7243";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              この内容でスコアを診断する
              <ArrowRightIcon />
            </button>
          </div>
        );
      case 4: {
        const result = getDiagnosisContent(formData.worry);
        return (
          <div style={{ backgroundColor: "white", borderRadius: "12px", overflow: "hidden" }}>
            <div style={{ backgroundColor: "#1f2937", color: "white", padding: "24px", textAlign: "center", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, right: 0, width: "160px", height: "160px", background: "rgba(237,114,67,0.2)", borderRadius: "50%", filter: "blur(48px)", transform: "translate(50%, -50%)" }} />
              <p style={{ color: "#9ca3af", fontSize: "12px", fontWeight: "bold", letterSpacing: "2px", marginBottom: "8px" }}>DIAGNOSIS RESULT</p>
              <h3 style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "16px" }}>あなたの経歴書スコア</h3>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: "8px", marginBottom: "16px" }}>
                <span style={{ fontSize: "60px", fontWeight: "800", color: "#fb923c", lineHeight: 1 }}>{result.score}</span>
                <span style={{ fontSize: "20px", color: "#9ca3af", fontWeight: "bold", marginBottom: "8px" }}>/ 100点</span>
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", backgroundColor: "rgba(127,29,29,0.5)", color: "#fecaca", padding: "4px 12px", borderRadius: "4px", fontSize: "14px", border: "1px solid #7f1d1d" }}>
                <AlertTriangleIcon />
                判定：{result.type}
              </div>
              <p style={{ fontSize: "14px", color: "#d1d5db", marginTop: "12px", lineHeight: 1.6, padding: "0 16px" }}>{result.desc}</p>
            </div>

            <div style={{ padding: "20px", display: "grid", gridTemplateColumns: "1fr", gap: "16px", backgroundColor: "#f9fafb" }}>
              {result.points.map((point) => (
                <div key={point} style={{ display: "flex", alignItems: "flex-start", gap: "10px", backgroundColor: "white", borderRadius: "8px", padding: "12px 14px", border: "1px solid #f3f4f6" }}>
                  <CheckCircleIcon />
                  <span style={{ color: "#374151", fontSize: "14px", lineHeight: 1.6 }}>{point}</span>
                </div>
              ))}
            </div>

            <form onSubmit={handleFinalSubmit} style={{ padding: "20px", backgroundColor: "white", display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ textAlign: "center", marginBottom: "4px" }}>
                <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "4px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <LockSmallIcon />
                  入力内容は暗号化して送信されます
                </p>
                <h3 style={{ fontSize: "18px", fontWeight: "bold", color: "#111827" }}>結果をメールで受け取る</h3>
                <p style={{ fontSize: "13px", color: "#6b7280" }}>あなた専用の改善レポートをお送りします</p>
              </div>

              <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "14px", color: "#374151" }}>
                お名前
                <input
                  required
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "12px", fontSize: "14px", outline: "none" }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#ed7243")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}
                />
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "14px", color: "#374151" }}>
                メールアドレス
                <input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "12px", fontSize: "14px", outline: "none" }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#ed7243")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}
                />
              </label>

              <button
                type="submit"
                style={{
                  width: "100%",
                  backgroundColor: "#ed7243",
                  color: "white",
                  fontWeight: "bold",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  fontSize: "16px",
                  transition: "transform 0.2s, background-color 0.2s",
                  marginTop: "8px",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc5f35";
                  e.currentTarget.style.transform = "scale(1.01)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ed7243";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                無料診断の結果をメールで受け取る
                <ArrowRightIcon />
              </button>

              <p style={{ fontSize: "11px", color: "#9ca3af", textAlign: "center", display: "flex", gap: "6px", justifyContent: "center", alignItems: "center", marginTop: "6px" }}>
                <LockSmallIcon />
                入力情報は診断目的でのみ使用し、第三者に提供しません。
              </p>
            </form>
          </div>
        );
      }
      case 5: {
        // Embed mode用の完了画面
        const result = getDiagnosisContent(formData.worry);
        const registerUrl = `https://truemee.jp/auth/register?email=${encodeURIComponent(formData.email)}&name=${encodeURIComponent(formData.name)}`;
        return (
          <div style={{ backgroundColor: "white", borderRadius: "12px", overflow: "hidden", textAlign: "center" }}>
            <div style={{ backgroundColor: "#10b981", color: "white", padding: "32px 24px" }}>
              <div style={{
                width: "64px",
                height: "64px",
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px"
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <path d="M22 4 12 14.01l-3-3" />
                </svg>
              </div>
              <h3 style={{ fontSize: "22px", fontWeight: "bold", marginBottom: "8px" }}>診断完了！</h3>
              <p style={{ fontSize: "14px", opacity: 0.9 }}>
                {formData.email} 宛に<br />詳細レポートをお送りしました
              </p>
            </div>

            <div style={{ padding: "24px", backgroundColor: "#f9fafb" }}>
              <div style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "16px",
                border: "1px solid #e5e7eb"
              }}>
                <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "8px" }}>あなたのスコア</p>
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: "4px" }}>
                  <span style={{ fontSize: "48px", fontWeight: "800", color: "#fb923c", lineHeight: 1 }}>{result.score}</span>
                  <span style={{ fontSize: "16px", color: "#9ca3af", fontWeight: "bold", marginBottom: "8px" }}>/ 100点</span>
                </div>
                <p style={{ fontSize: "13px", color: "#ef4444", marginTop: "8px" }}>{result.type}</p>
              </div>

              <p style={{ fontSize: "14px", color: "#374151", marginBottom: "16px", lineHeight: 1.6 }}>
                Truemeeに無料登録すると、<br />
                <strong>AIによる詳細な改善提案</strong>を受けられます
              </p>

              <a
                href={registerUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  width: "100%",
                  backgroundColor: "#ed7243",
                  color: "white",
                  fontWeight: "bold",
                  padding: "16px 24px",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontSize: "16px",
                  transition: "background-color 0.2s",
                }}
              >
                Truemeeで詳しく見る
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>

              <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "12px" }}>
                ※ 新しいタブで開きます
              </p>
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div style={{ backgroundColor: "#fdf5f2", borderRadius: "16px", padding: "24px", boxShadow: "0 24px 80px rgba(0,0,0,0.08)" }}>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <span
          style={{
            backgroundColor: "#fef3c7",
            color: "#b45309",
            fontSize: "12px",
            fontWeight: "bold",
            padding: "6px 14px",
            borderRadius: "9999px",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <ClockIcon />
          所要時間：約3分
        </span>
        <h2 style={{ fontSize: "24px", fontWeight: "800", color: "#111827", marginTop: "10px", marginBottom: "6px" }}>職務経歴書 無料診断フォーム</h2>
        <p style={{ color: "#6b7280", fontSize: "14px" }}>あなたの経歴書の弱点と改善ポイントを自動スコアリングします</p>
      </div>
      {renderFormStep()}
    </div>
  );
}

